import { useEffect, useState } from 'react';
import { isPointInPolygon } from 'geolib';
import { api } from 'lib';

const PDOK_PAND_URL = 'https://api.pdok.nl/kadaster/bag/ogc/v2/collections/pand/items';
// ~1 m around the position, so only the building(s) at that exact spot come back
const PDOK_BBOX_PADDING_DEG = 0.00001;

type Ring = [lng: number, lat: number][];

type PdokFeature = {
  geometry: { type: 'Polygon'; coordinates: Ring[] } | { type: 'MultiPolygon'; coordinates: Ring[][] };
};

type BuildingContactResponse = {
  candidates: {
    name: string;
    formattedAddress: string | null;
    phoneNumber: string | null;
    location: { lat: number; lng: number };
  }[];
};

export type BuildingContact = {
  name: string;
  address: string | null;
  phoneNumber: string;
  location: { lat: number; lng: number };
  // inside the footprint of the building at the position (the others are nearby)
  inBuilding: boolean;
};

export type Building = {
  // outer rings of the building footprint (a building can consist of several parts)
  rings: Ring[];
  // the ones in the building first, then the nearby ones, each in the order the server ranked them
  contacts: BuildingContact[];
};

const outerRings = ({ geometry }: PdokFeature): Ring[] => (
  geometry.type === 'Polygon' ? [geometry.coordinates[0]] : geometry.coordinates.map((polygon) => polygon[0])
);

const fetchRings = async ([lng, lat]: [number, number], signal: AbortSignal): Promise<Ring[] | null> => {

  const bbox = [
    lng - PDOK_BBOX_PADDING_DEG, lat - PDOK_BBOX_PADDING_DEG,
    lng + PDOK_BBOX_PADDING_DEG, lat + PDOK_BBOX_PADDING_DEG,
  ].join(',');
  const response = await fetch(`${PDOK_PAND_URL}?f=json&limit=10&bbox=${bbox}`, { signal });

  if (!response.ok) throw new Error(`PDOK request failed with status ${response.status}`);

  const { features } = await response.json() as { features: PdokFeature[] };
  const buildings = features.map(outerRings);

  return buildings.find((rings) => rings.some((ring) => isPointInPolygon([lng, lat], ring))) ?? buildings[0] ?? null;

};

// Places candidates come from the server within ~100 m of the position, closest first. Everything with a number is kept, so
// when the building itself can't be reached the others nearby are there; the ones inside the footprint come first.
const fetchContacts = async (alarmId: string, rings: Ring[], signal: AbortSignal): Promise<BuildingContact[]> => {

  const { data } = await api.post<BuildingContactResponse>('/specials/alarm-manager/building-contact', { alarmId }, { signal });

  const contacts = data.candidates.flatMap(({
    phoneNumber, name, formattedAddress, location,
  }) => (
    phoneNumber
      ? [{
        name,
        address: formattedAddress,
        phoneNumber,
        location,
        inBuilding: rings.some((ring) => isPointInPolygon([location.lng, location.lat], ring)),
      }]
      : []
  ));

  return [...contacts.filter(({ inBuilding }) => inBuilding), ...contacts.filter(({ inBuilding }) => !inBuilding)];

};

/**
 * Building footprint and contact details at the settled position.
 * The result is cleared when a lookup for a new position starts, so a previous building never shows for the new spot.
 */
export const useBuildingContact = (alarmId: string, position: [number, number] | null, enabled: boolean): Building | null => {

  const [building, setBuilding] = useState<Building | null>(null);
  const [lng, lat] = position ?? [];

  useEffect(() => {

    if (!enabled || lng === undefined || lat === undefined) return undefined;

    // a new settled position: the building of the previous one must not stay on screen while this one loads
    setBuilding(null);

    const controller = new AbortController();
    const { signal } = controller;

    const load = async () => {

      try {

        const rings = await fetchRings([lng, lat], signal);

        if (!rings) {

          setBuilding(null);

          return;

        }

        // draw the footprint right away, the contact follows when Places answered
        setBuilding({ rings, contacts: [] });

        const contacts = await fetchContacts(alarmId, rings, signal).catch((err) => {

          if (signal.aborted) throw err;

          // no permission (403) or Places unavailable: footprint only
          return [];

        });

        setBuilding({ rings, contacts });

      } catch {

        // aborted by a newer lookup: that one decides. PDOK unavailable: drop the old building, it is not where the person is now
        if (!signal.aborted) setBuilding(null);

      }

    };

    load();

    return () => controller.abort();

  }, [alarmId, enabled, lng, lat]);

  return building;

};
